import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip, Stack, Button, Divider } from '@mui/material';
import { Formik, Form } from 'formik';
import { isEqual } from 'lodash';

import { DeleteDialogUI, FormBase } from '../Interface';
import ItemCategoryTree from './ItemCategoryTree';
import ItemCategoryFields from './ItemCategoryFields';
import Yup from '../../utils/yup';
import itemCategoryService from '../../service/itemCategory.service';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { getNextVisOrder } from './itemCategoryUtils';

export const ItemCategoryPage = () => {
  const [selectedValues, setSelectedValues] = useState({});
  const [initValues, setInitValues] = useState({});
  const [defaultValues, setDefaultValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [viewField, setViewField] = useState(true);
  const [addField, setAddField] = useState(true);
  const [refetch, setRefetch] = useState(false);
  const [treeData, setTreeData] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    parentCategory: [],
  });

  const def = {
    name: '',
    parentId: null,
    externalCode: '',
    visOrder: '',
    active: true,
  };

  useEffect(() => {
    setDefaultValues(def);
    setDefaultSelectedValues(def);
    getItemCategory();
  }, []);

  useEffect(() => {
    getItemCategory();
  }, [refetch]);

  useEffect(() => {
    setSelectedValues(defaultSelectedValues);
  }, [defaultSelectedValues]);

  useEffect(() => {
    if (!addField) return;

    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues, addField]);

  const flattenCategoriesForAutocomplete = (categories, depth = 0) => {
    return categories.flatMap((category) => {
      return [
        {
          label: category.name,
          value: category.id,
          depth,
        },
        ...flattenCategoriesForAutocomplete(category.children || [], depth + 1),
      ];
    });
  };

  const getItemCategory = () => {
    itemCategoryService.getData().then((data) => {
      setRefetch(false);
      setTreeData(data);
      setAutoCompleteOptions((prev) => ({
        ...prev,
        parentCategory: flattenCategoriesForAutocomplete(data),
      }));
    });
  };

  const valuesToForm = () => {
    const { id, name, parentId, externalCode, visOrder, active } = selectedValues;

    setInitValues({
      id,
      name,
      parentId,
      externalCode,
      visOrder,
      active,
    });
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    parentId: Yup.string().nullable(),
    externalCode: Yup.string().nullable(),
    visOrder: Yup.number().required(),
    active: Yup.boolean(),
  });

  const handleSubmit = (values) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values);
    }
  };

  const updateObject = async (values) => {
    itemCategoryService.updateItem(values).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });
      setSelectedValues(data);
      setRefetch(true);
      setViewField(true);
      setEditButtonvisible(true);
    });
  };

  const handleInsertObject = async (values) => {
    itemCategoryService.createItem(values).then((data) => {
      addNotification({
        content: 'Sikeres mentés!',
        type: NotificationType.SUCCESS,
      });
      setSelectedValues(data);
      setRefetch(true);
      setAddField(true);
      setViewField(true);
    });
  };

  const onAddClick = () => {
    const nextVisOrder = getNextVisOrder(treeData, null);

    setSelectedValues(defaultSelectedValues);
    setInitValues({
      ...defaultValues,
      visOrder: nextVisOrder,
    });
    setViewField(false);
    setAddField(false);
  };

  const onModClick = () => {
    setViewField(false);
    setEditButtonvisible(false);
  };

  const handleRemoveElement = async () => {
    itemCategoryService.deleteItem(selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetch(true);
      setShowDeleteDialog(false);
      setSelectedValues(defaultSelectedValues);
    });
  };

  const isActive = selectedValues?.active;

  return (
    <>
      <FormBase
        name="ItemCategory"
        leftPanelContent={
          <>
            {selectedValues?.id && (
              <Stack justifyContent="space-between" direction="row" alignItems="center" mb={2}>
                <Typography fontWeight="bold" sx={{ fontSize: 18 }}>
                  {selectedValues.name}
                </Typography>
                <Chip
                  label={isActive ? 'Aktív' : 'Nem aktív'}
                  sx={{
                    color: isActive ? 'green' : 'red',
                    background: isActive ? 'lightgreen' : '#ffa2a4',
                    borderRadius: '5px',
                  }}
                  size="small"
                />
              </Stack>
            )}

            <Formik
              initialValues={initValues}
              validationSchema={validationSchema}
              validateOnChange={false}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ resetForm }) => (
                <Form>
                  <ItemCategoryFields
                    viewField={viewField}
                    autoCompleteOptions={autoCompleteOptions}
                    selectedValues={selectedValues}
                    treeData={treeData}
                  />

                  <Box>
                    <Divider sx={{ mb: 2, mt: 4 }} />
                    <Stack direction="row" justifyContent="space-between">
                      <Button
                        variant="outlined"
                        color="error"
                        sx={{ fontWeight: 'bold' }}
                        disabled={!viewField || operatorsDisabled}
                        onClick={() => setShowDeleteDialog(true)}
                      >
                        Törlés
                      </Button>
                      <Stack direction="row" spacing={2}>
                        <Button
                          variant="outlined"
                          sx={{ fontWeight: 'bold' }}
                          disabled={viewField}
                          onClick={() => {
                            setAddField(true);
                            setViewField(true);
                            setEditButtonvisible(true);

                            resetForm();
                          }}
                        >
                          Mégse
                        </Button>
                        <Button variant="contained" sx={{ fontWeight: 'bold' }} disabled={viewField} type="submit">
                          Mentés
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                </Form>
              )}
            </Formik>
          </>
        }
        rightPanelContent={
          <ItemCategoryTree
            setSelectedValues={setSelectedValues}
            treeData={treeData}
            operatorsDisabled={operatorsDisabled}
            viewField={viewField}
            onAddClick={onAddClick}
            onModClick={onModClick}
          />
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
